/**
 * Tutorial Composable - Laumeris
 * Système de tutoriel interactif
 */

import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useApi } from '@/composables/useApi'

export type TutorialType = 
  | 'team_onboarding'      // Équipe Laumeris - première connexion
  | 'client_onboarding'    // Clients - première connexion
  | 'first_quote'          // Premier devis reçu
  | 'first_invoice'        // Première facture reçue
  | 'first_document'       // Premier document reçu

export interface TutorialStep {
  id: string
  title: string
  description: string
  target?: string                    // Sélecteur CSS de l'élément cible
  action?: 'click' | 'navigate' | 'observe' | 'wait'  // Type d'action attendue
  actionTarget?: string              // Sélecteur ou route pour l'action
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'
  tip?: string
  highlight?: boolean                // Mettre en évidence l'élément
  waitForElement?: string           // Attendre qu'un élément apparaisse
  delay?: number                     // Délai avant de passer à l'étape suivante
  skipable?: boolean                 // L'étape peut être sautée
}

export interface Tutorial {
  id: TutorialType
  name: string
  steps: TutorialStep[]
}

// État global du tutoriel
const activeTutorial = ref<Tutorial | null>(null)
const currentStepIndex = ref(0)
const isVisible = ref(false)
const isPaused = ref(false)
const completedTutorials = ref<Set<string>>(new Set())

export function useTutorial() {
  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  const api = useApi()

  // Tutoriels disponibles
  const tutorials: Record<TutorialType, Tutorial> = {
    team_onboarding: {
      id: 'team_onboarding',
      name: 'Découverte de Laumeris',
      steps: [
        {
          id: 'welcome',
          title: 'Bienvenue dans Laumeris !',
          description: 'Nous allons vous guider à travers les fonctionnalités principales. Suivez les instructions et cliquez sur les éléments indiqués.',
          position: 'center',
          action: 'wait',
          delay: 3000
        },
        {
          id: 'sidebar',
          title: 'Le menu principal',
          description: 'Voici votre menu de navigation. Cliquez sur "Clients" pour découvrir la gestion des clients.',
          target: '[data-tutorial="clients-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="clients-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'clients-page',
          title: 'Gestion des clients',
          description: 'Ici vous pouvez voir tous vos clients. Chaque fiche contient les informations, documents et factures associés.',
          position: 'center',
          action: 'observe',
          waitForElement: '.clients-list, [data-tutorial="clients-list"]',
          delay: 2000
        },
        {
          id: 'back-dashboard',
          title: 'Retournons au tableau de bord',
          description: 'Cliquez sur "Tableau de bord" pour revenir à l\'accueil.',
          target: '[data-tutorial="dashboard-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="dashboard-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'documents',
          title: 'Documents sécurisés',
          description: 'Cliquez sur "Documents" pour accéder à la gestion des fichiers.',
          target: '[data-tutorial="documents-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="documents-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'documents-page',
          title: 'Espace documents',
          description: 'Tous vos documents sont stockés ici de manière sécurisée avec chiffrement de bout en bout.',
          position: 'center',
          action: 'observe',
          delay: 2000
        },
        {
          id: 'invoices',
          title: 'Facturation',
          description: 'Cliquez sur "Factures" pour gérer la facturation.',
          target: '[data-tutorial="invoices-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="invoices-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'invoices-page',
          title: 'Gestion des factures',
          description: 'Créez, envoyez et suivez toutes vos factures. Les paiements sont intégrés.',
          position: 'center',
          action: 'observe',
          delay: 2000
        },
        {
          id: 'quotes',
          title: 'Devis',
          description: 'Cliquez sur "Devis" pour gérer vos propositions commerciales.',
          target: '[data-tutorial="quotes-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="quotes-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'quotes-page',
          title: 'Gestion des devis',
          description: 'Créez des devis professionnels. Les clients peuvent les signer électroniquement.',
          position: 'center',
          action: 'observe',
          delay: 2000
        },
        {
          id: 'tickets',
          title: 'Support client',
          description: 'Cliquez sur "Assistance" pour voir les demandes d\'assistance.',
          target: '[data-tutorial="tickets-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="tickets-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'tickets-page',
          title: 'Demandes d\'assistance',
          description: 'Gérez les demandes de support de vos clients. Assignez, répondez et suivez chaque ticket.',
          position: 'center',
          action: 'observe',
          delay: 2000
        },
        {
          id: 'security',
          title: 'Sécurité',
          description: 'Cliquez sur "Sécurité" pour protéger votre compte.',
          target: '[data-tutorial="security-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="security-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'security-page',
          title: 'Centre de sécurité',
          description: 'Activez l\'authentification à deux facteurs et gérez vos appareils de confiance.',
          position: 'center',
          action: 'observe',
          delay: 2000,
          tip: 'Activez la 2FA pour une protection maximale !'
        },
        {
          id: 'complete',
          title: 'Tutoriel terminé !',
          description: 'Vous connaissez maintenant les bases de Laumeris. Explorez librement et n\'hésitez pas à nous contacter si besoin !',
          position: 'center',
          action: 'wait',
          delay: 0
        }
      ]
    },
    client_onboarding: {
      id: 'client_onboarding',
      name: 'Votre espace client',
      steps: [
        {
          id: 'welcome',
          title: 'Bienvenue dans votre espace !',
          description: 'Nous allons vous faire découvrir votre espace personnel. Suivez les instructions et cliquez sur les éléments indiqués.',
          position: 'center',
          action: 'wait',
          delay: 3000
        },
        {
          id: 'dashboard',
          title: 'Votre tableau de bord',
          description: 'Voici votre vue d\'ensemble. Vous y trouverez vos documents récents, factures et statistiques.',
          position: 'center',
          action: 'observe',
          delay: 2000
        },
        {
          id: 'documents',
          title: 'Vos documents',
          description: 'Cliquez sur "Documents" pour accéder à tous vos fichiers.',
          target: '[data-tutorial="documents-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="documents-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'documents-page',
          title: 'Espace documents',
          description: 'Tous les documents partagés avec vous sont ici. Consultez, téléchargez et organisez vos fichiers.',
          position: 'center',
          action: 'observe',
          delay: 2000
        },
        {
          id: 'invoices',
          title: 'Vos factures',
          description: 'Cliquez sur "Factures" pour voir vos factures.',
          target: '[data-tutorial="invoices-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="invoices-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'invoices-page',
          title: 'Gestion des factures',
          description: 'Consultez vos factures et leur statut. Vous pouvez payer en ligne de manière sécurisée.',
          position: 'center',
          action: 'observe',
          delay: 2000
        },
        {
          id: 'quotes',
          title: 'Vos devis',
          description: 'Cliquez sur "Devis" pour voir vos propositions commerciales.',
          target: '[data-tutorial="quotes-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="quotes-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'quotes-page',
          title: 'Vos devis',
          description: 'Retrouvez tous vos devis. Vous pouvez les consulter, les accepter ou les refuser.',
          position: 'center',
          action: 'observe',
          delay: 2000
        },
        {
          id: 'tickets',
          title: 'Besoin d\'aide ?',
          description: 'Cliquez sur "Assistance" pour contacter notre équipe.',
          target: '[data-tutorial="tickets-link"]',
          action: 'click',
          actionTarget: '[data-tutorial="tickets-link"]',
          position: 'right',
          highlight: true
        },
        {
          id: 'tickets-page',
          title: 'Support',
          description: 'Créez un ticket pour toute question. Notre équipe répond sous 24h.',
          position: 'center',
          action: 'observe',
          delay: 2000
        },
        {
          id: 'complete',
          title: 'C\'est terminé !',
          description: 'Vous connaissez maintenant votre espace. N\'hésitez pas à explorer et nous contacter si besoin !',
          position: 'center',
          action: 'wait',
          delay: 0
        }
      ]
    },
    first_quote: {
      id: 'first_quote',
      name: 'Votre premier devis',
      steps: [
        {
          id: 'intro',
          title: 'Vous avez reçu un devis !',
          description: 'Voyons ensemble comment consulter et répondre à un devis.',
          position: 'center',
          action: 'wait',
          delay: 2000
        },
        {
          id: 'preview',
          title: 'Prévisualisation',
          description: 'Vous pouvez consulter le devis directement ici. Utilisez le bouton pour afficher ou masquer le contenu.',
          target: '[data-tutorial="quote-preview"]',
          position: 'bottom',
          action: 'observe',
          delay: 3000,
          highlight: true
        },
        {
          id: 'details',
          title: 'Détails du devis',
          description: 'Les informations importantes comme le montant et la date de validité sont affichées ici.',
          target: '[data-tutorial="quote-details"]',
          position: 'left',
          action: 'observe',
          delay: 2000,
          highlight: true
        },
        {
          id: 'actions',
          title: 'Vos actions',
          description: 'Vous pouvez accepter ou refuser le devis en cliquant sur ces boutons. Nous ne ferons pas l\'action maintenant.',
          target: '[data-tutorial="quote-actions"]',
          position: 'top',
          action: 'observe',
          delay: 3000,
          highlight: true,
          tip: 'En acceptant, vous signez électroniquement le devis.'
        },
        {
          id: 'download',
          title: 'Téléchargement',
          description: 'Vous pouvez télécharger le devis en PDF à tout moment.',
          target: '[data-tutorial="quote-download"]',
          position: 'top',
          action: 'observe',
          delay: 2000,
          highlight: true
        },
        {
          id: 'complete',
          title: 'Vous savez tout !',
          description: 'Vous pouvez maintenant consulter et gérer vos devis en toute autonomie.',
          position: 'center',
          action: 'wait',
          delay: 0
        }
      ]
    },
    first_invoice: {
      id: 'first_invoice',
      name: 'Votre première facture',
      steps: [
        {
          id: 'intro',
          title: 'Vous avez reçu une facture !',
          description: 'Voyons ensemble comment la consulter et la payer.',
          position: 'center',
          action: 'wait',
          delay: 2000
        },
        {
          id: 'preview',
          title: 'Prévisualisation',
          description: 'Consultez votre facture directement ici. Le contenu est sécurisé.',
          target: '[data-tutorial="invoice-preview"]',
          position: 'bottom',
          action: 'observe',
          delay: 3000,
          highlight: true
        },
        {
          id: 'details',
          title: 'Détails de la facture',
          description: 'Le montant, la date d\'échéance et le statut de paiement sont affichés ici.',
          target: '[data-tutorial="invoice-details"]',
          position: 'left',
          action: 'observe',
          delay: 2000,
          highlight: true
        },
        {
          id: 'payment',
          title: 'Paiement en ligne',
          description: 'Ce bouton vous permet de payer en ligne par carte bancaire. Le paiement est 100% sécurisé via Stripe.',
          target: '[data-tutorial="invoice-pay"]',
          position: 'top',
          action: 'observe',
          delay: 3000,
          highlight: true,
          tip: 'Nous ne déclencherons pas le paiement maintenant, ne vous inquiétez pas !'
        },
        {
          id: 'bank-transfer',
          title: 'Paiement par virement',
          description: 'Vous pouvez aussi payer par virement bancaire. Les coordonnées sont disponibles ici.',
          target: '[data-tutorial="invoice-bank"]',
          position: 'top',
          action: 'observe',
          delay: 2000,
          highlight: true
        },
        {
          id: 'download',
          title: 'Téléchargement',
          description: 'Téléchargez votre facture en PDF pour vos archives.',
          target: '[data-tutorial="invoice-download"]',
          position: 'top',
          action: 'observe',
          delay: 2000,
          highlight: true
        },
        {
          id: 'complete',
          title: 'Vous savez tout !',
          description: 'Vous pouvez maintenant consulter et payer vos factures en toute sérénité.',
          position: 'center',
          action: 'wait',
          delay: 0
        }
      ]
    },
    first_document: {
      id: 'first_document',
      name: 'Votre premier document',
      steps: [
        {
          id: 'intro',
          title: 'Nouveau document !',
          description: 'Voyons comment consulter vos documents partagés.',
          position: 'center',
          action: 'wait',
          delay: 2000
        },
        {
          id: 'preview',
          title: 'Prévisualisation',
          description: 'Le document s\'affiche directement ici. Vous pouvez zoomer et naviguer.',
          target: '[data-tutorial="document-preview"]',
          position: 'bottom',
          action: 'observe',
          delay: 3000,
          highlight: true
        },
        {
          id: 'details',
          title: 'Informations',
          description: 'Les détails du document : type, date d\'ajout, taille...',
          target: '[data-tutorial="document-details"]',
          position: 'left',
          action: 'observe',
          delay: 2000,
          highlight: true
        },
        {
          id: 'download',
          title: 'Téléchargement',
          description: 'Téléchargez le document sur votre appareil.',
          target: '[data-tutorial="document-download"]',
          position: 'top',
          action: 'observe',
          delay: 2000,
          highlight: true
        },
        {
          id: 'complete',
          title: 'C\'est tout !',
          description: 'Vous savez maintenant consulter vos documents. Simple, non ?',
          position: 'center',
          action: 'wait',
          delay: 0
        }
      ]
    }
  }

  // Step courante
  const currentStep = computed(() => {
    if (!activeTutorial.value) return null
    return activeTutorial.value.steps[currentStepIndex.value]
  })

  const isLastStep = computed(() => {
    if (!activeTutorial.value) return false
    return currentStepIndex.value === activeTutorial.value.steps.length - 1
  })

  const progress = computed(() => {
    if (!activeTutorial.value) return 0
    return ((currentStepIndex.value + 1) / activeTutorial.value.steps.length) * 100
  })

  // Démarrer un tutoriel
  async function startTutorial(type: TutorialType) {
    const tutorial = tutorials[type]
    if (!tutorial) {
      console.error(`Tutorial ${type} not found`)
      return
    }

    // Vérifier si déjà complété
    if (completedTutorials.value.has(type) && type.includes('onboarding')) {
      console.log(`Tutorial ${type} already completed`)
      return
    }

    activeTutorial.value = tutorial
    currentStepIndex.value = 0
    isVisible.value = true
    isPaused.value = false

    // Attendre le premier step si delay
    await processCurrentStep()
  }

  // Passer à l'étape suivante
  async function nextStep() {
    if (!activeTutorial.value) return

    if (isLastStep.value) {
      await completeTutorial()
    } else {
      currentStepIndex.value++
      await processCurrentStep()
    }
  }

  // Étape précédente
  function prevStep() {
    if (currentStepIndex.value > 0) {
      currentStepIndex.value--
    }
  }

  // Traiter l'étape courante
  async function processCurrentStep() {
    const step = currentStep.value
    if (!step) return

    // Attendre l'élément si nécessaire
    if (step.waitForElement) {
      await waitForElement(step.waitForElement)
    }

    // Si c'est une étape d'observation avec délai automatique
    if (step.action === 'observe' && step.delay) {
      setTimeout(() => {
        if (!isPaused.value && activeTutorial.value) {
          nextStep()
        }
      }, step.delay)
    }

    // Si c'est une étape d'attente avec délai
    if (step.action === 'wait' && step.delay && step.delay > 0) {
      setTimeout(() => {
        if (!isPaused.value && activeTutorial.value) {
          nextStep()
        }
      }, step.delay)
    }

    // Si c'est une action de clic, on attend le clic de l'utilisateur
    if (step.action === 'click' && step.actionTarget) {
      setupClickListener(step.actionTarget)
    }
  }

  // Attendre qu'un élément apparaisse
  function waitForElement(selector: string, timeout = 5000): Promise<Element | null> {
    return new Promise((resolve) => {
      const element = document.querySelector(selector)
      if (element) {
        resolve(element)
        return
      }

      const observer = new MutationObserver((mutations, obs) => {
        const el = document.querySelector(selector)
        if (el) {
          obs.disconnect()
          resolve(el)
        }
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true
      })

      setTimeout(() => {
        observer.disconnect()
        resolve(null)
      }, timeout)
    })
  }

  // Écouter les clics sur l'élément cible
  let clickHandler: ((e: Event) => void) | null = null

  function setupClickListener(selector: string) {
    // Nettoyer l'ancien listener
    if (clickHandler) {
      document.removeEventListener('click', clickHandler, true)
    }

    clickHandler = (e: Event) => {
      const target = e.target as Element
      const targetElement = document.querySelector(selector)
      
      if (targetElement && (targetElement === target || targetElement.contains(target))) {
        // L'utilisateur a cliqué sur le bon élément
        document.removeEventListener('click', clickHandler!, true)
        clickHandler = null
        
        // Petit délai pour laisser la navigation se faire
        setTimeout(() => {
          nextStep()
        }, 500)
      }
    }

    document.addEventListener('click', clickHandler, true)
  }

  // Terminer le tutoriel
  async function completeTutorial() {
    if (!activeTutorial.value) return

    const tutorialId = activeTutorial.value.id
    completedTutorials.value.add(tutorialId)

    // Sauvegarder côté serveur
    try {
      await api.post('/auth/tutorial-completed', { tutorialId })
    } catch (error) {
      console.error('Error saving tutorial completion:', error)
    }

    // Nettoyer
    cleanup()
  }

  // Passer le tutoriel
  function skipTutorial() {
    completeTutorial()
  }

  // Mettre en pause
  function pauseTutorial() {
    isPaused.value = true
  }

  // Reprendre
  function resumeTutorial() {
    isPaused.value = false
    processCurrentStep()
  }

  // Nettoyer
  function cleanup() {
    if (clickHandler) {
      document.removeEventListener('click', clickHandler, true)
      clickHandler = null
    }
    activeTutorial.value = null
    currentStepIndex.value = 0
    isVisible.value = false
    isPaused.value = false
  }

  // Vérifier si un tutoriel doit être lancé
  async function checkAndStartTutorial() {
    if (!authStore.user) return
    if (isVisible.value) return // Déjà en cours

    const user = authStore.user

    // Vérifier si c'est la première connexion (tutorialCompleted = false)
    if (user.tutorialCompleted === false) {
      // Déterminer le type de tutoriel
      const tutorialType = user.userType === 'client' ? 'client_onboarding' : 'team_onboarding'
      
      // Petit délai pour laisser la page se charger
      setTimeout(() => {
        startTutorial(tutorialType)
      }, 1000)
    }
  }

  // Démarrer un tutoriel contextuel
  function startContextualTutorial(type: TutorialType) {
    const contextualTypes: TutorialType[] = ['first_quote', 'first_invoice', 'first_document']
    
    if (contextualTypes.includes(type)) {
      // Vérifier si déjà vu (localStorage pour les contextuels)
      const key = `tutorial_${type}_completed`
      if (localStorage.getItem(key)) {
        return
      }

      setTimeout(() => {
        startTutorial(type)
      }, 1500)
    }
  }

  // Marquer un tutoriel contextuel comme complété
  function markContextualCompleted(type: TutorialType) {
    const key = `tutorial_${type}_completed`
    localStorage.setItem(key, 'true')
  }

  // Réinitialiser un tutoriel (pour le refaire via mail)
  function resetTutorial(type: TutorialType) {
    completedTutorials.value.delete(type)
    const key = `tutorial_${type}_completed`
    localStorage.removeItem(key)
  }

  return {
    // State
    activeTutorial,
    currentStep,
    currentStepIndex,
    isVisible,
    isPaused,
    isLastStep,
    progress,
    
    // Actions
    startTutorial,
    nextStep,
    prevStep,
    skipTutorial,
    completeTutorial,
    pauseTutorial,
    resumeTutorial,
    cleanup,
    checkAndStartTutorial,
    startContextualTutorial,
    markContextualCompleted,
    resetTutorial
  }
}

